import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Router, NavigationEnd, NavigationSkipped } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  catchError,
  EMPTY,
  filter,
  map,
  of,
  pairwise,
  startWith,
  switchMap,
  withLatestFrom,
} from 'rxjs';
import { routerSegments } from '../../consts/routes';
import { WallsService } from '../../services/walls.service';
import { OrgWallsActions } from '../state/org-walls/org-walls.actions';
import { orgWallsFeature } from '../state/org-walls/org-walls.feature';

import { OrgWallEntry } from '../state/org-walls/org-walls.models';
import { WallsModels } from '../../models/walls.model';

const wallsUrl = `/${routerSegments.home}/${routerSegments.walls}`;

const isWallsUrl = (url: string): boolean => url.startsWith(wallsUrl);

const areQueriesEqual = (
  left: WallsModels.GetOrgWallsQuery,
  right: WallsModels.GetOrgWallsQuery,
): boolean => {
  if ((left.query ?? '') !== (right.query ?? '')) {
    return false;
  }

  if (
    (left.limit ?? WallsModels.ORG_WALLS_PAGE_LIMIT) !==
    (right.limit ?? WallsModels.ORG_WALLS_PAGE_LIMIT)
  ) {
    return false;
  }

  if ((left.latestTs ?? null) !== (right.latestTs ?? null)) {
    return false;
  }

  const leftOrgIds: string[] = left.orgIds ?? [];
  const rightOrgIds: string[] = right.orgIds ?? [];

  if (leftOrgIds.length !== rightOrgIds.length) {
    return false;
  }

  return leftOrgIds.every((orgId, index) => orgId === rightOrgIds[index]);
};

export const loadOrgWallsOnWallsEnter = createEffect(
  (router = inject(Router), store = inject(Store)) => {
    return router.events.pipe(
      filter((event): event is NavigationEnd | NavigationSkipped =>
        event instanceof NavigationEnd || event instanceof NavigationSkipped,
      ),
      map(() => router.url),
      startWith(router.url),
      pairwise(),
      filter(([prev, curr]) => !isWallsUrl(prev) && isWallsUrl(curr)),
      withLatestFrom(store.select(orgWallsFeature.selectLoaded)),
      filter(([, loaded]) => !loaded),
      map(() => OrgWallsActions.loadOrgWalls({})),
    );
  },
  { functional: true },
);

export const resetOrgWallsOnWallsLeave = createEffect(
  (router = inject(Router)) => {
    return router.events.pipe(
      filter((event): event is NavigationEnd | NavigationSkipped =>
        event instanceof NavigationEnd || event instanceof NavigationSkipped,
      ),
      map(() => router.url),
      startWith(router.url),
      pairwise(),
      filter(([prev, curr]) => isWallsUrl(prev) && !isWallsUrl(curr)),
      map(() => OrgWallsActions.resetOrgWalls()),
    );
  },
  { functional: true },
);

export const loadOrgWalls = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
  ) => {
    return actions$.pipe(
      ofType(OrgWallsActions.loadOrgWalls),
      withLatestFrom(
        store.select(orgWallsFeature.selectLoaded),
        store.select(orgWallsFeature.selectQuery),
      ),
      switchMap(([{ query = {} }, loaded, currentQuery]) => {
        if (loaded && areQueriesEqual(query, currentQuery)) {
          return EMPTY;
        }

        return of(OrgWallsActions.loadOrgWallsRequest({ query }));
      }),
    );
  },
  { functional: true },
);

export const getNextOrgWallsPage = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
  ) => {
    return actions$.pipe(
      ofType(OrgWallsActions.getNextPage),
      withLatestFrom(
        store.select(orgWallsFeature.selectQuery),
        store.select(orgWallsFeature.selectLatestTs),
        store.select(orgWallsFeature.selectIsLoading),
        store.select(orgWallsFeature.selectIsLastPage),
      ),
      filter(([, , , isLoading, isLastPage]) => !isLoading && !isLastPage),
      map(([, query, latestTs]) =>
        OrgWallsActions.loadOrgWalls({
          query: {
            ...query,
            latestTs: latestTs ?? undefined,
          },
        }),
      ),
    );
  },
  { functional: true },
);

export const loadOrgWallsRequest = createEffect(
  (
    actions$ = inject(Actions),
    wallsService = inject(WallsService),
  ) => {
    return actions$.pipe(
      ofType(OrgWallsActions.loadOrgWallsRequest),
      switchMap(({ query }) =>
        wallsService.getOrgWalls(query).pipe(
          map((walls) =>
            OrgWallsActions.loadOrgWallsSuccess({
              walls: walls.map(
                (wall): OrgWallEntry => ({
                  ...wall,
                  id: `${wall.accountId}:${wall._id}`,
                }),
              ),
              query,
            }),
          ),
          catchError((error: Error) =>
            of(OrgWallsActions.loadOrgWallsFail({ error: error.message })),
          ),
        ),
      ),
    );
  },
  { functional: true },
);
